import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Manager } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { getAuthToken, getRealtimeUrl, onAuthTokenChange, SOCKET_PATH } from './config';
import { normalizeRealtimeEvent } from './eventNormalizer';
import { invalidateAllKnownQueries } from './queryInvalidation';

export const CONNECTION_STATUS = Object.freeze({
  IDLE: 'idle',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  UNAUTHORIZED: 'unauthorized',
  ERROR: 'error',
});

const PRESENCE_PING_INTERVAL_MS = 25000;

/**
 * Tempo máximo para o namespace principal entrar (engine aberto + CONNECT
 * aceito). Sem esse limite a UI ficaria em "connecting" para sempre quando o
 * servidor aceita o WebSocket mas recusa/perde o namespace (proxy, auth).
 */
const CONNECT_TIMEOUT_MS = 20000;

const RealtimeContext = createContext(null);

/**
 * Provider único de tempo real.
 *
 * Um `Manager` do Socket.IO multiplexa os namespaces `/` e `/boards` em uma
 * única conexão de transporte por aba. Também cuida de:
 * - reconectar quando o token muda (login/logout);
 * - ressincronizar as queries do React Query ao (re)conectar;
 * - enviar presença periódica.
 */
export const RealtimeProvider = ({ children, url }) => {
  const queryClient = useQueryClient();
  const managerRef = useRef(null);
  const mainRef = useRef(null);
  const boardsRef = useRef(null);
  const pingRef = useRef(null);
  const connectTimeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const roomRefs = useRef(new Map());

  const [status, setStatus] = useState(CONNECTION_STATUS.IDLE);
  const [lastError, setLastError] = useState(null);
  const [socketEpoch, setSocketEpoch] = useState(0);

  // Espelho síncrono do status: os callbacks do Socket.IO são criados uma única
  // vez e leriam um `status` congelado no closure.
  const statusRef = useRef(CONNECTION_STATUS.IDLE);

  const changeStatus = useCallback((nextStatus) => {
    statusRef.current = nextStatus;
    setStatus(nextStatus);
  }, []);

  const stopPing = useCallback(() => {
    if (pingRef.current) {
      clearInterval(pingRef.current);
      pingRef.current = null;
    }
  }, []);

  const clearConnectTimeout = useCallback(() => {
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }
  }, []);

  const teardown = useCallback(() => {
    stopPing();
    clearConnectTimeout();

    const manager = managerRef.current;
    const main = mainRef.current;
    const boards = boardsRef.current;

    mainRef.current = null;
    boardsRef.current = null;
    managerRef.current = null;

    try {
      main?.removeAllListeners();
      main?.disconnect();
    } catch (error) {
      console.error('[Realtime] Erro ao desconectar namespace principal:', error);
    }

    try {
      boards?.removeAllListeners();
      boards?.disconnect();
    } catch (error) {
      console.error('[Realtime] Erro ao desconectar namespace de boards:', error);
    }

    // `disconnect()` nos namespaces só fecha o engine quando ambos saem; fechar o
    // Manager explicitamente (`disconnect()` é o alias público de `_close()`; não
    // existe `close()` na API do socket.io-client 4) evita engines órfãos — e o
    // aviso "WebSocket is closed before the connection is established" ao
    // remontar em StrictMode.
    try {
      manager?.removeAllListeners();
      manager?.disconnect();
    } catch (error) {
      console.error('[Realtime] Erro ao encerrar o Manager de tempo real:', error);
    }
  }, [clearConnectTimeout, stopPing]);

  const startPing = useCallback(
    (socket) => {
      stopPing();
      pingRef.current = setInterval(() => {
        if (socket?.connected) socket.emit('presence:ping');
      }, PRESENCE_PING_INTERVAL_MS);
    },
    [stopPing]
  );

  const connect = useCallback(() => {
    const token = getAuthToken();

    if (!token) {
      teardown();
      changeStatus(CONNECTION_STATUS.UNAUTHORIZED);
      setLastError(null);
      return;
    }

    if (managerRef.current) return;

    changeStatus(CONNECTION_STATUS.CONNECTING);

    try {
      const manager = new Manager(url || getRealtimeUrl(), {
        path: SOCKET_PATH,
        // WebSocket primeiro (menor latência), com fallback automático para
        // polling: sem `tryAllTransports` o engine.io-client aborta a conexão
        // quando o WS é bloqueado por proxy/CDN, sem tentar o próximo transport.
        transports: ['websocket', 'polling'],
        tryAllTransports: true,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        randomizationFactor: 0.5,
        timeout: 20000,
        autoConnect: false,
      });

      managerRef.current = manager;

      // `auth` como função: cada (re)conexão envia o token atual do localStorage,
      // cobrindo renovação de sessão sem recriar o Manager.
      const auth = (callback) => callback({ token: getAuthToken() });

      const main = manager.socket('/', { auth });
      const boards = manager.socket('/boards', { auth });

      mainRef.current = main;
      boardsRef.current = boards;

      /**
       * Rede de segurança: se o engine abrir mas o namespace não entrar (proxy
       * sem Upgrade, auth recusada, etc.) o status não pode ficar em "connecting".
       */
      const scheduleConnectTimeout = () => {
        clearConnectTimeout();
        connectTimeoutRef.current = setTimeout(() => {
          connectTimeoutRef.current = null;

          if (!mountedRef.current) return;
          if (mainRef.current !== main || main.connected) return;
          if (statusRef.current === CONNECTION_STATUS.UNAUTHORIZED) return;

          changeStatus(CONNECTION_STATUS.ERROR);
          setLastError('Tempo esgotado ao conectar em tempo real');
        }, CONNECT_TIMEOUT_MS);
      };

      main.on('connect', () => {
        if (!mountedRef.current) return;
        clearConnectTimeout();
        changeStatus(CONNECTION_STATUS.CONNECTED);
        setLastError(null);
        setSocketEpoch((value) => value + 1);
        invalidateAllKnownQueries(queryClient);
        startPing(main);
      });

      main.on('disconnect', (reason) => {
        if (!mountedRef.current) return;
        stopPing();
        changeStatus(CONNECTION_STATUS.DISCONNECTED);

        if (reason === 'io server disconnect') {
          // O servidor encerrou a conexão: os namespaces precisam reconectar
          // manualmente (o Manager só reconecta sozinho em quedas de transporte).
          main.connect();
          boards.connect();
        }

        scheduleConnectTimeout();
      });

      main.on('connect_error', (error) => {
        if (!mountedRef.current) return;
        const message = error?.message || 'Falha na conexão em tempo real';
        const unauthorized = /authentication/i.test(message);
        changeStatus(unauthorized ? CONNECTION_STATUS.UNAUTHORIZED : CONNECTION_STATUS.ERROR);
        setLastError(message);
      });

      boards.on('connect_error', (error) => {
        console.error('[Realtime] Erro de conexão no namespace de boards:', error?.message);
      });

      // `Manager#open()` abre apenas o transport: o pacote CONNECT de cada
      // namespace só é enviado por `Socket#connect()` (o Manager está com
      // `autoConnect: false`). Sem estas duas chamadas nada é conectado.
      main.connect();
      boards.connect();

      scheduleConnectTimeout();
    } catch (error) {
      console.error('[Realtime] Erro ao configurar Socket.IO:', error);
      changeStatus(CONNECTION_STATUS.ERROR);
      setLastError(error?.message || 'Erro ao configurar conexão em tempo real');
    }
  }, [changeStatus, clearConnectTimeout, queryClient, startPing, stopPing, teardown, url]);

  useEffect(() => {
    mountedRef.current = true;
    connect();

    const unsubscribeAuth = onAuthTokenChange(() => {
      teardown();
      setSocketEpoch((value) => value + 1);
      connect();
    });

    return () => {
      mountedRef.current = false;
      unsubscribeAuth();
      teardown();
    };
  }, [connect, teardown]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;

      if (!mainRef.current) {
        connect();
      } else if (!mainRef.current.connected) {
        mainRef.current.connect();
        boardsRef.current?.connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [connect]);

  // Logout em SPA remove o token sem recarregar a página; este watcher garante
  // que a conexão seja encerrada (e restabelecida após um novo login).
  useEffect(() => {
    const interval = setInterval(() => {
      const token = getAuthToken();

      if (!token && managerRef.current) {
        teardown();
        setSocketEpoch((value) => value + 1);
        changeStatus(CONNECTION_STATUS.UNAUTHORIZED);
        setLastError(null);
        return;
      }

      if (token && !managerRef.current) {
        connect();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [changeStatus, connect, teardown]);

  const subscribe = useCallback((eventType, handler) => {
    const socket = mainRef.current;
    if (!socket || !eventType || typeof handler !== 'function') return () => {};

    const listener = (payload) => {
      try {
        const normalized = normalizeRealtimeEvent(eventType, payload);
        if (normalized) handler(normalized.data, normalized);
      } catch (error) {
        console.error(`[Realtime] Erro ao processar evento '${eventType}':`, error);
      }
    };

    socket.on(eventType, listener);

    return () => {
      try {
        socket.off(eventType, listener);
      } catch (error) {
        console.error(`[Realtime] Erro ao remover listener '${eventType}':`, error);
      }
    };
  }, []);

  const subscribeBoard = useCallback((eventName, handler) => {
    const socket = boardsRef.current;
    if (!socket || !eventName || typeof handler !== 'function') return () => {};

    const listener = (payload) => {
      try {
        handler(payload);
      } catch (error) {
        console.error(`[Realtime] Erro ao processar evento de board '${eventName}':`, error);
      }
    };

    socket.on(eventName, listener);

    return () => {
      try {
        socket.off(eventName, listener);
      } catch (error) {
        console.error(`[Realtime] Erro ao remover listener de board '${eventName}':`, error);
      }
    };
  }, []);

  const joinRoom = useCallback(
    (scope, id) =>
      new Promise((resolve) => {
        const socket = mainRef.current;
        if (!socket?.connected) {
          resolve({ ok: false, error: 'NOT_CONNECTED' });
          return;
        }

        socket.emit('join', { scope, id }, (response) => {
          resolve(response || { ok: false, error: 'NO_RESPONSE' });
        });
      }),
    []
  );

  const leaveRoom = useCallback((scope, id) => {
    const socket = mainRef.current;
    if (socket?.connected) socket.emit('leave', { scope, id });
  }, []);

  /** Emite um evento de presença/contexto pelo socket principal. */
  const emit = useCallback((eventName, payload) => {
    const socket = mainRef.current;
    if (socket?.connected) socket.emit(eventName, payload);
  }, []);

  /**
   * Entra na sala de forma referenciada: vários componentes podem usar a mesma
   * sala e apenas o último a sair dispara `leave`.
   * @returns {Function} release
   */
  const acquireRoom = useCallback(
    (scope, id) => {
      if (!scope || !id) return () => {};

      const key = `${scope}:${id}`;
      const count = roomRefs.current.get(key) || 0;
      roomRefs.current.set(key, count + 1);

      if (count === 0) joinRoom(scope, id);

      let released = false;

      return () => {
        if (released) return;
        released = true;

        const current = (roomRefs.current.get(key) || 1) - 1;

        if (current <= 0) {
          roomRefs.current.delete(key);
          leaveRoom(scope, id);
        } else {
          roomRefs.current.set(key, current);
        }
      };
    },
    [joinRoom, leaveRoom]
  );

  const joinBoard = useCallback(
    (boardId) =>
      new Promise((resolve) => {
        const socket = boardsRef.current;
        if (!socket?.connected) {
          resolve({ ok: false, error: 'NOT_CONNECTED' });
          return;
        }

        socket.emit('join-board', boardId, (response) => {
          resolve(response || { ok: true });
        });
      }),
    []
  );

  const leaveBoard = useCallback((boardId) => {
    const socket = boardsRef.current;
    if (socket?.connected) socket.emit('leave-board', boardId);
  }, []);

  const emitBoard = useCallback((eventName, payload) => {
    const socket = boardsRef.current;
    if (socket?.connected) socket.emit(eventName, payload);
  }, []);

  // Reentra nas salas adquiridas após reconexão/troca de token.
  useEffect(() => {
    if (status !== CONNECTION_STATUS.CONNECTED) return;

    roomRefs.current.forEach((_count, key) => {
      const separator = key.indexOf(':');
      if (separator === -1) return;

      joinRoom(key.slice(0, separator), key.slice(separator + 1));
    });
  }, [status, socketEpoch, joinRoom]);

  const isConnected = status === CONNECTION_STATUS.CONNECTED;

  const value = useMemo(
    () => ({
      status,
      isConnected,
      lastError,
      socketEpoch,
      subscribe,
      subscribeBoard,
      joinRoom,
      leaveRoom,
      acquireRoom,
      emit,
      joinBoard,
      leaveBoard,
      emitBoard,
    }),
    [
      status,
      isConnected,
      lastError,
      socketEpoch,
      subscribe,
      subscribeBoard,
      joinRoom,
      leaveRoom,
      acquireRoom,
      emit,
      joinBoard,
      leaveBoard,
      emitBoard,
    ]
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
};

export const useRealtime = () => {
  const context = useContext(RealtimeContext);

  if (!context) {
    throw new Error('useRealtime deve ser usado dentro de um RealtimeProvider');
  }

  return context;
};

export default RealtimeProvider;
